// Rules and math for the invoice logo page (Settings > Billing > Logo,
// Roadmap 1847). No browser APIs here, so everything can be tested.

/** The invoice PDF (invoicePdf.ts) draws the logo in a box of at most 60 x 20 mm,
 * 10 mm from the top-left corner of an A4 page. */
export const PDF_LOGO_BOX_MM = { width: 60, height: 20, left: 10, top: 10 };
export const A4_MM = { width: 210, height: 297 };

/** The saved logo always fills that box exactly (3:1), so what the person sees
 * in the frame is what prints. 1200 px across 60 mm = about 500 dpi. */
export const FRAME = { width: 1200, height: 400 };

/** Smaller sizes tried when the PNG is too big (the client portal reads logos
 * up to 2 MB). */
export const OUTPUT_SIZES = [
  { width: 1200, height: 400 },
  { width: 900, height: 300 },
  { width: 600, height: 200 },
];
export const MAX_SAVED_BYTES = 1_500_000;

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const RECOMMENDED_MIN_WIDTH = 600;

const ACCEPTED: { types: string[]; extensions: string[] } = {
  types: [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/svg+xml",
    "image/webp",
  ],
  extensions: [".png", ".jpg", ".jpeg", ".svg", ".webp"],
};

export const ACCEPT_ATTRIBUTE = [
  ...ACCEPTED.types,
  ...ACCEPTED.extensions,
].join(",");

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
};

export const isSvgFile = (file: { name: string; type: string }) =>
  file.type === "image/svg+xml" || extensionOf(file.name) === ".svg";

/** A plain-English problem with the chosen file, or null when it can be used. */
export const logoFileProblem = (file: {
  name: string;
  type: string;
  size: number;
}): string | null => {
  const ext = extensionOf(file.name);
  const typeOk = ACCEPTED.types.includes((file.type || "").toLowerCase());
  const extOk = ACCEPTED.extensions.includes(ext);
  if (!typeOk && !extOk) {
    if (ext === ".heic" || ext === ".heif" || /heic|heif/i.test(file.type)) {
      return "iPhone photos (HEIC) can't be used. Please save the logo as PNG or JPG and try again.";
    }
    if (ext === ".pdf" || file.type === "application/pdf") {
      return "PDF files can't be used as a logo. Please save it as PNG, JPG or SVG and try again.";
    }
    return "This file type can't be used. Please choose a PNG, JPG, SVG or WebP image.";
  }
  if (file.size > MAX_FILE_BYTES) {
    return `This file is ${formatBytes(
      file.size,
    )}; the limit is 10 MB. Please choose a smaller image.`;
  }
  if (file.size === 0) {
    return "This file is empty. Please choose another image.";
  }
  return null;
};

export const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
};

/** Size (mm) the current invoice PDF gives an image of naturalWidth x
 * naturalHeight pixels. Mirrors buildInvoicePdf (which compares pixels with mm,
 * so an image under 20 px tall stays that many mm tall). */
export const pdfLogoSize = (naturalWidth: number, naturalHeight: number) => {
  if (!naturalWidth || !naturalHeight) return { width: 0, height: 0 };
  const ratio = naturalWidth / naturalHeight;
  let height = Math.min(naturalHeight, PDF_LOGO_BOX_MM.height);
  let width = height * ratio;
  if (width > PDF_LOGO_BOX_MM.width) {
    width = PDF_LOGO_BOX_MM.width;
    height = width / ratio;
  }
  return { width, height };
};

export type Rect = { x: number; y: number; width: number; height: number };

/** Where the (trimmed) picture sits in the 1200 x 400 frame. */
export type Placement = {
  /** 1 = the whole picture just fits the frame. */
  zoom: number;
  x: number;
  y: number;
};

export type Align = "left" | "center" | "right";

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 3;

export const fitScale = (crop: { width: number; height: number }) =>
  Math.min(FRAME.width / crop.width, FRAME.height / crop.height);

export const fillZoom = (crop: { width: number; height: number }) =>
  Math.max(FRAME.width / crop.width, FRAME.height / crop.height) /
  fitScale(crop);

export const drawnSize = (
  crop: { width: number; height: number },
  zoom: number,
) => {
  const scale = fitScale(crop) * zoom;
  return { width: crop.width * scale, height: crop.height * scale, scale };
};

const clampAxis = (pos: number, size: number, frame: number) => {
  // Smaller than the frame: keep it fully inside. Bigger: no empty gap.
  if (size <= frame) return Math.min(Math.max(pos, 0), frame - size);
  return Math.min(Math.max(pos, frame - size), 0);
};

export const clampPlacement = (
  crop: { width: number; height: number },
  placement: Placement,
): Placement => {
  const zoom = Math.min(Math.max(placement.zoom, MIN_ZOOM), MAX_ZOOM);
  const { width, height } = drawnSize(crop, zoom);
  return {
    zoom,
    x: clampAxis(placement.x, width, FRAME.width),
    y: clampAxis(placement.y, height, FRAME.height),
  };
};

const alignedX = (width: number, align: Align) => {
  if (align === "center") return (FRAME.width - width) / 2;
  if (align === "right") return FRAME.width - width;
  return 0;
};

/** Start position: fits the frame, centred vertically, left like the invoice. */
export const initialPlacement = (
  crop: { width: number; height: number },
  align: Align = "left",
): Placement => {
  const { width, height } = drawnSize(crop, 1);
  return { zoom: 1, x: alignedX(width, align), y: (FRAME.height - height) / 2 };
};

export const alignPlacement = (
  crop: { width: number; height: number },
  placement: Placement,
  align: Align,
): Placement => {
  const { width, height } = drawnSize(crop, placement.zoom);
  return clampPlacement(crop, {
    zoom: placement.zoom,
    x: alignedX(width, align),
    y: (FRAME.height - height) / 2,
  });
};

/** Changes the zoom keeping the picture's centre where it was. */
export const zoomPlacement = (
  crop: { width: number; height: number },
  placement: Placement,
  zoom: number,
): Placement => {
  const before = drawnSize(crop, placement.zoom);
  const nextZoom = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
  const after = drawnSize(crop, nextZoom);
  const cx = placement.x + before.width / 2;
  const cy = placement.y + before.height / 2;
  return clampPlacement(crop, {
    zoom: nextZoom,
    x: cx - after.width / 2,
    y: cy - after.height / 2,
  });
};

export const movePlacement = (
  crop: { width: number; height: number },
  placement: Placement,
  dx: number,
  dy: number,
): Placement =>
  clampPlacement(crop, {
    ...placement,
    x: placement.x + dx,
    y: placement.y + dy,
  });

/** Which alignment button looks pressed (null after dragging). */
export const currentAlign = (
  crop: { width: number; height: number },
  placement: Placement,
): Align | null => {
  const { width, height } = drawnSize(crop, placement.zoom);
  if (Math.abs(placement.y - (FRAME.height - height) / 2) > 1) return null;
  for (const align of ["left", "center", "right"] as Align[]) {
    if (Math.abs(placement.x - alignedX(width, align)) <= 1) return align;
  }
  return null;
};

export type PrintQuality = {
  level: "sharp" | "good" | "blurry";
  dpi: number;
  label: string;
};

/** How sharp the printed logo will be: picture pixels per printed inch. */
export const printQuality = (
  crop: { width: number; height: number },
  zoom: number,
): PrintQuality => {
  const { width } = drawnSize(crop, zoom);
  const printedInches = ((width / FRAME.width) * PDF_LOGO_BOX_MM.width) / 25.4;
  const dpi = Math.round(crop.width / printedInches);
  if (dpi >= 250) return { level: "sharp", dpi, label: "Sharp when printed" };
  if (dpi >= 150) return { level: "good", dpi, label: "Good when printed" };
  return { level: "blurry", dpi, label: "May look blurry" };
};

/** Same check for an already saved logo (drawn by the PDF code as is). */
export const savedLogoQuality = (
  naturalWidth: number,
  naturalHeight: number,
): PrintQuality => {
  const { width } = pdfLogoSize(naturalWidth, naturalHeight);
  const dpi = width ? Math.round(naturalWidth / (width / 25.4)) : 0;
  if (dpi >= 250) return { level: "sharp", dpi, label: "Sharp when printed" };
  if (dpi >= 150) return { level: "good", dpi, label: "Good when printed" };
  return { level: "blurry", dpi, label: "May look blurry" };
};

export type PixelSummary = {
  hasTransparency: boolean;
  /** Box around the logo itself, without empty / plain-background edges. */
  bounds: Rect | null;
  /** Nearly all visible pixels are white: hard to see on a white invoice. */
  mostlyWhite: boolean;
};

const isNearWhite = (r: number, g: number, b: number) =>
  r > 235 && g > 235 && b > 235;

/** Looks at RGBA pixels: transparency, where the logo really is, and whether
 * it is (nearly) all white. Edges are "empty" when transparent or, for
 * pictures without transparency, the same colour as the top-left corner. */
export const summarizePixels = (
  data: Uint8ClampedArray | number[],
  width: number,
  height: number,
): PixelSummary => {
  let hasTransparency = false;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 250) {
      hasTransparency = true;
      break;
    }
  }
  const bgR = data[0];
  const bgG = data[1];
  const bgB = data[2];
  const tolerance = 24;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  let visible = 0;
  let white = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      let content: boolean;
      if (hasTransparency) {
        content = a > 16;
      } else {
        content =
          Math.abs(r - bgR) > tolerance ||
          Math.abs(g - bgG) > tolerance ||
          Math.abs(b - bgB) > tolerance;
      }
      if (a > 16) {
        visible++;
        if (isNearWhite(r, g, b)) white++;
      }
      if (content) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  const bounds =
    maxX < 0
      ? null
      : { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  // Only transparent pictures can be "invisible": a white JPG is a white box.
  const mostlyWhite = hasTransparency && visible > 0 && white / visible > 0.92;
  return { hasTransparency, bounds, mostlyWhite };
};

/** Scales bounds found on a smaller sample back to the full picture, with a
 * small margin, inside the picture. Null when trimming would change little. */
export const trimRect = (
  sampleBounds: Rect | null,
  sampleWidth: number,
  sampleHeight: number,
  fullWidth: number,
  fullHeight: number,
): Rect | null => {
  if (!sampleBounds) return null;
  const sx = fullWidth / sampleWidth;
  const sy = fullHeight / sampleHeight;
  const margin = Math.max(fullWidth, fullHeight) * 0.01;
  const x = Math.max(0, Math.floor(sampleBounds.x * sx - margin));
  const y = Math.max(0, Math.floor(sampleBounds.y * sy - margin));
  const right = Math.min(
    fullWidth,
    Math.ceil((sampleBounds.x + sampleBounds.width) * sx + margin),
  );
  const bottom = Math.min(
    fullHeight,
    Math.ceil((sampleBounds.y + sampleBounds.height) * sy + margin),
  );
  const rect = { x, y, width: right - x, height: bottom - y };
  if (rect.width < 2 || rect.height < 2) return null;
  const removed = 1 - (rect.width * rect.height) / (fullWidth * fullHeight);
  return removed < 0.03 ? null : rect;
};

/** Sample size used to look at the pixels (keeps big photos fast). */
export const sampleSize = (width: number, height: number, max = 600) => {
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/** SVGs without a width/height get one from their viewBox, long side = 2400. */
export const svgRenderSize = (
  width: number | null,
  height: number | null,
  viewBox: string | null,
  longSide = 2400,
): { width: number; height: number } => {
  let w = width && width > 0 ? width : 0;
  let h = height && height > 0 ? height : 0;
  if ((!w || !h) && viewBox) {
    const parts = viewBox
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
      if (w && !h) h = (w * parts[3]) / parts[2];
      else if (h && !w) w = (h * parts[2]) / parts[3];
      else {
        w = parts[2];
        h = parts[3];
      }
    }
  }
  if (!w || !h) {
    w = w || 300;
    h = h || 100;
  }
  const scale = longSide / Math.max(w, h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
};

/** Short plain-English notes about the picture the person picked. */
export const pictureNotes = (input: {
  width: number;
  height: number;
  trimmed: boolean;
  mostlyWhite: boolean;
  quality: PrintQuality;
  isSvg: boolean;
}): string[] => {
  const notes: string[] = [];
  if (input.mostlyWhite) {
    notes.push(
      "This logo is almost all white, so it will be hard to see on the white invoice. Use the colour or dark version of your logo.",
    );
  }
  if (!input.isSvg && input.quality.level === "blurry") {
    notes.push(
      `This picture is small (${input.width} × ${input.height} px) for the size it prints at. It may look blurry. A file at least ${RECOMMENDED_MIN_WIDTH} px wide works best, or make the logo smaller.`,
    );
  }
  if (input.height > input.width * 1.2) {
    notes.push(
      "Tall or square logos print small, because the logo space on the invoice is 3 times wider than it is tall. A wide version of your logo fills it better.",
    );
  }
  return notes;
};
